import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useLazyGetFactImplReportQuery,
  type ModelFactImplReport,
} from '@app/api/Analytics';
import { ACTUAL_IMPLEMENTATION } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type { FiltersInterface } from '@app/pages/Analytics/ActualImplementation/types';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import { useShownReportPeriod } from '@app/pages/Analytics/hooks';
import type { HandleSearchDataProps } from '@app/pages/Analytics/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useActualImplementation = () => {
  const [tableData, setTableData] = useState<ModelFactImplReport>();

  const { setShownPeriod } = useShownReportPeriod(ACTUAL_IMPLEMENTATION);
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [form] = useForm<FiltersInterface>();

  const [getFactImplReport, { isLoading }] = useLazyGetFactImplReportQuery();

  const handleSearchData = useCallback(
    async ({ filterMonth, filterYear }: HandleSearchDataProps) => {
      try {
        const result = await getFactImplReport({
          ActiveBranchId,
          filterMonth,
          filterYear,
        }).unwrap();

        setTableData(result);
        setShownPeriod({ filterMonth, filterYear });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getFactImplReport, setShownPeriod, showNotification]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData(DEFAULT_FILTERS_DATA);
  }, [form, handleSearchData]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;
      handleSearchData({
        filterMonth,
        filterYear,
      });
    },
    [handleSearchData]
  );

  return {
    handleReset,
    form,
    handleSubmitForm,
    tableData,
    handleSearchData,
    isLoading,
  };
};
